import React from 'react';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Router from 'next/router';
import { useChannelsEnabled } from '@components/Channels/helpers';
import { ChannelAbout } from '@components/Channels/main/About';
import { ChannelCurators } from '@components/Channels/main/Curators';
import { ChannelHero } from '@components/Channels/main/Hero';
import { ChannelInfo } from '@components/Channels/main/Info';
import { ChannelMembers } from '@components/Channels/main/Members';
import { ChannelPosts } from '@components/Channels/posts/ChannelPosts';
import { ChannelFloatingButton } from '@components/Channels/posts/PostFloatingButton';
import { ChannelPostInput } from '@components/Channels/posts/PostInput';
import { useChannelEngagement } from '@components/Channels/useChannelEngagement';
import { Seo, type SeoProps } from '@components/Seo';
import { Spinner } from '@components/basics/Spinner';
import { ChannelPageDocument, type ChannelPageQuery, fetchGQL, gql } from '@graphql/reactQuery';

/*#__PURE__*/ gql(/* GraphQL */ `
  query ChannelPage($channelSlug: String!) {
    channelBySlug(nameSlug: $channelSlug) {
      id
      nameSlug
      webappUri
      name
      description
      published
      staticProfileImage {
        id
        url
      }
      profileImage {
        id
        url
      }
      ...ChannelHero
      ...ChannelInfo
      ...ChannelAbout
    }
  }
`);

const ISR_REVALIDATE_PERIOD = 20;

type PathParams = {
  channelSlug: string;
};

interface PageProps {
  seo: SeoProps;
  channel: NonNullable<ChannelPageQuery['channelBySlug']>;
}

export const getStaticProps: GetStaticProps<PageProps, PathParams> = async ({ params }) => {
  const { channelSlug } = params || {};

  if (!channelSlug || Array.isArray(channelSlug)) {
    return {
      redirect: {
        destination: '/404',
        permanent: true,
      },
    };
  }

  return Promise.all([
    fetchGQL(ChannelPageDocument, {
      variables: { channelSlug },
    }),
  ]).then(async ([data]) => {
    const channel = data.data.channelBySlug;
    if (!channel || !channel.published) {
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
        revalidate: ISR_REVALIDATE_PERIOD,
      };
    }

    const profileImageUri = channel.staticProfileImage?.url ?? channel.profileImage?.url ?? null;

    const props: PageProps = {
      seo: {
        title: `${channel.name} | Sound.xyz`,
        twitterHandle: null,
        description: channel.description,
        image: profileImageUri,
        audioUrl: null,
        url: channel.webappUri,
      },
      channel,
    };

    return {
      props,
      revalidate: ISR_REVALIDATE_PERIOD,
    };
  });
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: true,
  };
};

export default function ChannelPage({ channel, seo }: Partial<PageProps>) {
  const { enabled: channelsEnabled, isLoading } = useChannelsEnabled();

  useChannelEngagement({ channelId: channel?.id });

  React.useEffect(() => {
    if (!channelsEnabled && !isLoading) {
      Router.replace('/');
    }
  }, [channelsEnabled, isLoading]);

  // Only used on fallback, when cache can't be found
  if (!channel) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <Seo {...seo} />
      <div className="flex min-h-screen flex-col bg-white">
        <div className="w-full bg-white">
          <ChannelHero channelFrag={channel} />

          <div className="relative flex min-h-page justify-center overflow-x-clip md:min-h-md-page">
            <div className="flex w-full gap-20 px-4 pb-6 md:px-6 dt:max-w-content">
              <div className="min-w-0 flex-1">
                <div className="relative -top-14 z-above3 mx-auto mt-0 flex w-full max-w-7xl flex-col lg:-top-32 lt:px-12">
                  <ChannelInfo channelFrag={channel} />

                  <ChannelPostInput channelId={channel.id} />

                  <div className="mt-10 flex w-full flex-col gap-6">
                    <ChannelPosts channelId={channel.id} />
                  </div>
                </div>
              </div>

              <div className="relative hidden h-full min-w-0 flex-[0_0_304px] lt:inline">
                <div className="sticky mt-3 hidden h-fit min-w-0 overflow-x-hidden lt:flex">
                  <div className="flex w-full min-w-0 flex-col">
                    <div className="mt-8 flex w-full flex-col gap-10">
                      <ChannelAbout channelFrag={channel} />
                      <ChannelCurators channelId={channel.id} />
                      <ChannelMembers channelId={channel.id} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ChannelFloatingButton channelId={channel.id} />
    </>
  );
}
